import { useState } from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PincodeInput from 'react-pinco';

import LoadingButton from '@mui/lab/LoadingButton';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { OneTimePasswordStyled } from './styled';
import AlertMessage from '../../../shared/AlertMessage';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import { RootState } from '../../../../store';
import { cookieShareDomain, useOtpVerifyMutation, useSingInMethodMutation } from '../../../../services/singnin.service';
import { saveOyanaAuth } from '../../../../features/singin/singin-slice';
import { setRedirect } from '../../../../features/redirect';

const OneTimePassword = () => {
  const {
    redirect: { redirectTo }
  } = useSelector((state: RootState) => state);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [otpVerify, { isLoading: isLoadingOTP, isError }] = useOtpVerifyMutation();
  const [singInMethod, { isLoading: isLoadingRetry }] = useSingInMethodMutation();
  const [otp, setOTP] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [cookies, setCookie, removeCookie] = useCookies([
    'oya_oyana_access_token',
    'oya.oyanaid',
    'oya.method1',
    'oya.method2',
    'oya.singInMethod'
  ]);

  const submit = () => {
    if (!cookies['oya.oyanaid']) return navigate('/login');

    otpVerify({ code: otp, oyanaid: cookies['oya.oyanaid'] })
      .unwrap()
      .then(resp => {
        const oya_oyana_access_token = resp.token;
        setCookie('oya_oyana_access_token', oya_oyana_access_token, {
          domain: cookieShareDomain,
          /**
           * secure é definido como true para ambientes que não sejam local, garantindo que o cookie seja transmitido apenas por conexões HTTPS e também possibilitando testes locais
           */
          secure: process.env.REACT_APP_STAGE !== 'local',
          maxAge: 86400
        });
        removeCookie('oya.oyanaid');
        removeCookie('oya.method1');
        removeCookie('oya.method2');
        removeCookie('oya.singInMethod');
        dispatch(saveOyanaAuth(oya_oyana_access_token));
        if (redirectTo) {
          navigate(redirectTo);
          dispatch(setRedirect(''));
        } else {
          navigate('/');
        }
      })
      .catch(err => {
        setErrorMessage(err.data.message);
      });
  };

  const retry = (e: any) => {
    e.preventDefault();
    singInMethod({ id: cookies['oya.oyanaid'], method: cookies['oya.singInMethod'] })
      .unwrap()
      .catch((err: any) => {
        setErrorMessage(err.data.message);
      });
  };

  return (
    <>
      <form
        id="form-3"
        onSubmit={e => e.preventDefault()}
        onKeyPress={e => {
          if (e.code === 'Enter') {
            submit();
          }
        }}
        autoComplete="on"
      >
        <Typography variant="body1" sx={{ mb: 4 }}>
          Nós enviamos seu código para{' '}
          <strong>{cookies['oya.singInMethod'] === 'email' ? cookies['oya.method1'] : cookies['oya.method2']}</strong>
        </Typography>
        <OneTimePasswordStyled>
          <PincodeInput
            onChange={(value: any) => {
              setOTP(value.join(''));
            }}
            numInputs={6}
            containerStyle="Pincode-container"
            inputStyle="Pincode-input"
            errorStyle="Pincode-error"
            isInputNum={true}
            hasErrored={isError}
            sx={{ borderRadius: 0 }}
          />
        </OneTimePasswordStyled>

        <Typography variant="body1" sx={{ mb: 4 }}>
          Não recebeu?{' '}
          <Link onClick={e => retry(e)} sx={{ cursor: 'pointer' }}>
            Reenviar o código
          </Link>
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="contained" startIcon={<ArrowBackIcon />} color="secondary" onClick={() => navigate(-1)}>
            Voltar
          </Button>

          <LoadingButton
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => submit()}
            loading={isLoadingOTP}
            disabled={otp.length === 6 ? false : true}
          >
            Continuar
          </LoadingButton>
        </Box>
        {isLoadingRetry && <AlertMessage text="O código foi reenviado." type="success" />}
        {isError && <AlertMessage text={errorMessage} textButton="Tente novamente" type="error" />}
      </form>
    </>
  );
};

export default OneTimePassword;
